import { AIRCRAFT_CATEGORY_CLASS_TYPE, AIRCRAFT_PROVIDER, US_SHORT_CODE } from "@/config/constants";
import { words } from "lodash";
import dayjs from "dayjs";
import { usStates } from "./usStates";

export const isApplySnap = (breakPointWidth = 1192, breakPointHeight = 714) => {
	if (!window) return false;
	const { innerHeight, innerWidth } = window;
	if (innerWidth > innerHeight && innerWidth >= breakPointWidth && innerHeight >= breakPointHeight) {
		return true;
	}
	return false;
};

export const getAvatarShortenedName = (name) => {
	if (typeof name === "string" && name.trim() !== "") {
		const ws = words(name);
		const { length } = ws;
		let sh;
		if (length > 1) {
			sh = ws[0].charAt(0) + ws[length - 1].charAt(0);
		} else {
			sh = ws[0].charAt(0) + ws[0].charAt(ws[0].length - 1);
		}
		return sh.toUpperCase();
	}
	return "";
};

export const combineAddress = (value = {}) => {
	let { address = "", address2 = "", city = "", state = "", postalcode = "", zip_code = "", country = {} } = value;

	let items = [];
	if (address) items.push(address);
	if (address2) items.push(address2);
	if (city) items.push(city);
	if (state) {
		if (country?.short_code === US_SHORT_CODE && state.length === 2) {
			let stateValue = usStates.find((v) => v.abbreviation === state);
			items.push(stateValue?.name ?? state);
		} else {
			items.push(state);
		}
	}
	if (postalcode || zip_code) items.push(postalcode || zip_code);
	if (country?.english_name) items.push(country?.english_name);

	return items.join(", ");
};

export const formatPhoneNumberStr = (phoneNumberString) => {
	let cleaned = ("" + phoneNumberString).replace(/\D/g, "");
	let match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
	if (match) {
		let intlCode = match[1] ? "+1 " : "";
		return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
	}
	return phoneNumberString;
};

export const maskCharacter = (str, mask, n = 1) => {
	// Slice the string and replace with
	// mask then add remaining string
	return ("" + str).slice(0, -n).replace(/./g, mask) + " " + ("" + str).slice(-n);
};

export const convertVariableCodeToString = (text = "") => {
	const items = text.split("_");
	return items.join(" ");
};

export const groupDuplicateValuesArray = (arr = []) => {
	const arr2 = arr.reduce((values = [], current) => {
		const i = values.findIndex((x) => x.code === current.code);
		return (i === -1 ? values.push({ ...current }) : false, values);
	}, []);
	return arr2;
};

export const formatCurrency = (value, currency = "USD", maximumFractionDigits = undefined) => {
	try {
		const data = new Intl.NumberFormat("us-US", {
			style: "currency",
			currency,
			currencyDisplay: "narrowSymbol",
			maximumFractionDigits,
		}).format(value);

		return data;
	} catch (e) {
		return value;
	}
};

export const getBase64 = (file) =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => {
			resolve(reader.result);
		};
		reader.onerror = (error) => reject(error);
	});

export const formatPhoneNumber = (value = "") => {
	let phone = value;

	const input = value.replace(/\D/g, "").substring(0, 10); // First ten digits of input only
	const areaCode = input.substring(0, 3);
	const middle = input.substring(3, 6);
	const last = input.substring(6, 10);

	if (input.length > 6) {
		phone = `+ (${areaCode}) ${middle} - ${last}`;
	} else if (input.length > 3) {
		phone = `(${areaCode}) ${middle}`;
	} else if (input.length > 0) {
		phone = `(${areaCode}`;
	}

	return phone;
};

// Latest moment a leg occupies: the window end when a departure window is used.
export const getLegEndDatetime = (leg = {}) =>
	(leg?.departureWindow ? leg?.departure_window_datetime : leg?.departure_datetime) || leg?.departure_datetime;

// true when `value` is before `min` (both ISO strings), compared at minute precision.
export const isBeforeDatetime = (value, min) => {
	if (!value || !min) {
		return false;
	}
	return dayjs(value).startOf("minute").isBefore(dayjs(min).startOf("minute"));
};

// Contacts have no _id, so they are identified by their position in user.contacts.
export const getContactKey = (contact = {}) => `${contact?.name ?? ""}|${contact?.phone ?? ""}`;

// Contacts identical in both name and phone are indistinguishable to the user, keep the first.
export const getUniqueContacts = (contacts = []) => {
	const seen = new Set();
	return contacts.filter((contact) => {
		const key = getContactKey(contact);
		if (seen.has(key)) {
			return false;
		}
		seen.add(key);
		return true;
	});
};

// Maps each selected contact to its own index in allContacts, aligned with selectedContacts
// (-1 when the contact no longer exists there). Contacts sharing the same phone number
// resolve to distinct indexes.
export const mapContactsToIndexes = (selectedContacts = [], allContacts = []) => {
	const used = new Set();

	return selectedContacts.map((contact) => {
		const key = getContactKey(contact);
		const index = allContacts.findIndex((v, i) => !used.has(i) && getContactKey(v) === key);
		if (index > -1) {
			used.add(index);
		}
		return index;
	});
};

// Select values (as strings) for the contacts that still exist in allContacts.
export const resolveContactIndexes = (selectedContacts = [], allContacts = []) =>
	mapContactsToIndexes(selectedContacts, allContacts)
		.filter((index) => index > -1)
		.map(String);

// Selected contacts that were removed from the profile, so they no longer match any option.
export const getOrphanContacts = (selectedContacts = [], allContacts = []) => {
	const indexes = mapContactsToIndexes(selectedContacts, allContacts);
	return selectedContacts.filter((_, i) => indexes[i] === -1);
};

export const formatNumber = (value = 0) => {
	return Number(value).toLocaleString();
};

export const convertKnotToMph = (value = 0) => {
	return Math.floor(value * 1.15078);
};

export const sortedCategory = (categories = []) => {
	const classTypeOrder = [
		AIRCRAFT_CATEGORY_CLASS_TYPE.CLASS_TURBO_PROPS,
		AIRCRAFT_CATEGORY_CLASS_TYPE.CLASS_LIGHT_JET,
		AIRCRAFT_CATEGORY_CLASS_TYPE.CLASS_MIDSIZE,
		AIRCRAFT_CATEGORY_CLASS_TYPE.CLASS_SUPER_MIDSIZE,
		AIRCRAFT_CATEGORY_CLASS_TYPE.CLASS_HEAVY,
		AIRCRAFT_CATEGORY_CLASS_TYPE.CLASS_LONG_RANGE,
	];
	const providerOrder = [AIRCRAFT_PROVIDER.ROYAL_AIR, AIRCRAFT_PROVIDER.PARTNER];

	const sortProvidersInside = (category) => {
		if (!category.aircraft_type_list) return category;

		category.aircraft_type_list = [...category.aircraft_type_list].sort((a, b) => {
			const aIdx = providerOrder.indexOf(a.aircraft_provider);
			const bIdx = providerOrder.indexOf(b.aircraft_provider);

			return (aIdx === -1 ? Infinity : aIdx) - (bIdx === -1 ? Infinity : bIdx);
		});

		return category;
	};

	return [...categories].map(sortProvidersInside).sort((a, b) => {
		const aIndex = classTypeOrder.indexOf(a.classtype);
		const bIndex = classTypeOrder.indexOf(b.classtype);
		if (aIndex !== -1 && bIndex !== -1) {
			return aIndex - bIndex;
		}
		if (aIndex !== -1) return -1;
		if (bIndex !== -1) return 1;
		return 0;
	});
};

export const additionalTitle = {
	is_bring_pet: "I’m bringing a pet",
	is_bring_oversize_luggage: "I’m bringing oversized luggage",
	is_interest_empty_leg: "I’m interested in empty legs",
};
